/**
 * Inline SVG icons — the app's only icon primitive.
 *
 * Replaces Shoelace's icon element, which fetched one SVG per glyph at runtime
 * from /shoelace/assets/icons/. Every icon therefore painted late, after
 * components.js executed and a separate request resolved, and it needed a
 * registerIconLibrary("") shim because Preact clobbered the reflected
 * `library` property on hydration. Inlining the same Bootstrap glyphs at build
 * time removes both problems, and was the last thing holding Shoelace in.
 *
 * Glyph data lives in ./icons.gen.ts, generated by src/scripts/build-icons.ts.
 * `name` is typed against that registry, so a glyph the app does not ship is a
 * type error rather than a silently blank icon.
 */
import { type IconName, ICONS } from "./icons.gen.ts"

export type { IconName }

type IconProps = {
  name: IconName
  /** Extra classes — sizing is `1em`, so `text-3xl` scales the glyph. */
  class?: string
  style?: string
  /**
   * Accessible name. Omit for decorative icons sitting beside a text label,
   * which is the common case; they render `aria-hidden`.
   */
  label?: string
}

export function Icon({ name, class: className, style, label }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="currentColor"
      class={className}
      style={style ? `flex-shrink:0;${style}` : "flex-shrink:0"}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : "true"}
      // The markup is build-time output from src/scripts/build-icons.ts over a
      // vendored SVG set — a closed union of literals, never user input. There
      // is no other way to hand Preact a fragment of pre-parsed SVG.
      // deno-lint-ignore react-no-danger
      dangerouslySetInnerHTML={{ __html: ICONS[name] }} // nosemgrep: react-dangerouslysetinnerhtml
    />
  )
}

/**
 * Narrows a value to a registered glyph name. `ComponentChildren` already
 * admits `string`, so a `name | node` prop cannot be split on `typeof` alone —
 * this checks the registry instead.
 */
export function isIconName(v: unknown): v is IconName {
  return typeof v === "string" && v in ICONS
}

/**
 * Icon markup as a string, for the few places that build DOM by hand rather
 * than rendering JSX (`src/lib/toast.ts`, the Lit components).
 */
export function iconMarkup(name: IconName, className?: string): string {
  const cls = className ? ` class="${className}"` : ""
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16" fill="currentColor"${cls} style="flex-shrink:0" aria-hidden="true">${
    ICONS[name]
  }</svg>`
}
